<template>
  <div
    ref="slider"
    class="relative overflow-hidden pt-4 md:px-8 lg:px-16"
    :class="{ 'pl-4 pr-8': showNextSlideOnMobile, 'px-4': !showNextSlideOnMobile }"
  >
    <div class="swiper-wrapper flex">
      <article
        v-for="(item, index) in items"
        :key="index"
        class="swiper-slide"
      >
        <!-- Product design -->
        <div v-if="item.cardView === 'product'">
          <a
            :href="item.url"
            :target="item.openInNewWindow ? '_blank' : '_self'"
            :rel="item.openInNewWindow ? 'noopener noreferrer' : ''"
            class="block text-center item-link"
            @click="onItemClick(item)"
          >
            <Picture
              v-if="item.image"
              v-bind="item.image"
              :mobile="[500, 480]"
              :options="{mode: 'max'}"
              lazy
              class="item-picture relative block h-0 pt-full bg-white transition-transform duration-150 ease-out mb-6"
              img-class="absolute top-0 left-0 w-full h-full object-contain p-6"
              width="500"
              height="480"
            />

            <div class="relative text-center">
              <h3 class="lg:text-xl md:text-s ltr:font-ltr">{{ item.name }}</h3>
            </div>
          </a>
          <AdimoButton
            v-if="item.adimoButton"
            :model="item.adimoButton"
          />
        </div>

        <!-- Recipe design -->
        <a
          v-if="item.cardView === 'recipe'"
          :href="item.url"
          :target="item.openInNewWindow ? '_blank' : '_self'"
          :rel="item.openInNewWindow ? 'noopener noreferrer' : ''"
          class="h-0 flex flex-col justify-end relative group bg-center bg-no-repeat bg-cover pt-6/5"
          @click="onItemClick(item)"
        >
          <Picture
            v-if="item.image"
            v-bind="item.image"
            :mobile="[640, 480]"
            lazy
            img-class="absolute top-0 left-0 w-full h-full object-cover"
            width="640"
            height="480"
          />

          <div class="absolute inset-0 bg-gradient-black-to-transparant w-full pointer-events-none" />

          <div class="relative text-white p-4 group-hover:-translate-y-2 transform duration-200">
            <div class="text-xs pb-2">{{ item.tags }}</div>
            <div class="text-xl">{{ item.name }}</div>
          </div>

          <div
            v-if="item.preparationTime"
            class="absolute top-0 left-0 flex items-center mx-4 mt-6 text-xs bg-white text-primary py-2 px-3 rounded-full"
          >
            <span class="icon-clock ltr:mr-1 rtl:ml-1" />
            <span>{{ item.preparationTime }}</span>
          </div>
        </a>

        <story-card
          v-if="item.cardView !== 'product' && item.cardView !== 'recipe'"
          :model="item"
        />
      </article>
    </div>

    <div
      v-show="elementIsVisibleInViewport"
      class="swiper-pagination"
    />

    <button
      v-show="elementIsVisibleInViewport"
      class="swiper-button-prev"
    >
      <span class="icon-chevron-left text-white relative z-20" />
    </button>
    <button
      v-show="elementIsVisibleInViewport"
      class="swiper-button-next"
    >
      <span class="icon-chevron-right text-white relative z-20" />
    </button>
  </div>
</template>

<script>
import Swiper from 'swiper'
import StoryCard from '@/components/story-card.vue'
import AdimoButton from '@/components/AdimoButton.vue'
import Picture from '@/components/Picture.vue'

export default {
  components: {
    StoryCard,
    AdimoButton,
    Picture
  },
  props: {
    items: {
      type: Array,
      required: true
    },
    sliderTitle: {
      type: String,
      required: true
    },
    showNextSlideOnMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isMobile: window.matchMedia('(max-width: 639px)').matches,
      isTablet: window.matchMedia('(min-width:640px) and (max-width: 1023px)').matches,
      isDesktop: window.matchMedia('(min-width:1024px) and (max-width: 1279px)').matches,
      isLargeDesktop: window.matchMedia('(min-width: 1280px)').matches
    }
  },
  computed: {
    isPagingVisibleOnMobile() {
      return this.isMobile && this.items.length > 1
    },
    isPagingVisibleOnTablet() {
      return this.isTablet && this.items.length > 2
    },
    isPagingVisibleOnDesktop() {
      return this.isDesktop && this.items.length > 3
    },
    isPagingVisibleOnLargeDesktop() {
      return this.isLargeDesktop && this.items.length > 4
    },
    elementIsVisibleInViewport() {
      return this.isPagingVisibleOnMobile || this.isPagingVisibleOnTablet || this.isPagingVisibleOnDesktop || this.isPagingVisibleOnLargeDesktop
    }
  },
  mounted() {
    const settings = {
      initialSlide: 0,
      grabCursor: this.items.length > 1,
      slidesPerView: 1,
      spaceBetween: 16,
      pagination: {
        el: '.swiper-pagination'
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
      },
      breakpoints: {
        640: {
          grabCursor: this.items.length > 2,
          slidesPerView: 2,
          spaceBetween: 24
        },
        1024: {
          grabCursor: this.items.length > 3,
          slidesPerView: 3
        },
        1280: {
          grabCursor: this.items.length > 4,
          slidesPerView: 4
        }
      }
    }

    this.slider = new Swiper(this.$refs.slider, settings)
    this.slider.on('slideChange', () => {
      const trackingData = {
        event: 'sliderScroll',
        interactionType: (this.slider.swipeDirection === undefined) ? 'click' : 'swipe',
        sliderTitle: this.sliderTitle,
        direction: (this.slider.activeIndex > this.slider.previousIndex) ? 'right' : 'left'
      }
      window.dataLayer.push(trackingData)
    })
  },
  methods: {
    getRecipeImageBackgroundStyle(imageUrl, preset1) {
      if (!imageUrl) {
        return ''
      }

      return 'background-image: url('.concat(imageUrl, '?preset=', preset1, ')')
    },
    onItemClick(item) {
      const trackingData = {
        event: 'sliderItemClick',
        sliderTitle: this.sliderTitle,
        sliderItemTitle: item.name
      }

      window.dataLayer.push(trackingData)
    }
  }
}
</script>
